/**
 * Granular permissions engine — the single source of truth for "may X do Y?".
 *
 * Rules:
 *  1. A super-admin holds 100% of the permissions, always.
 *  2. An admin starts from a baseline and is tuned per-account with custom
 *     configuration flags (`user.permissionOverrides`), except for a small set
 *     of actions reserved to super-admins.
 *  3. A soldier may only view tasks and update the status of tasks assigned to
 *     them. Overrides never widen a soldier's role.
 *  4. An account that is not approved, or whose status is not `active`, holds
 *     no permissions at all — regardless of role.
 */
import { USER_PERMISSIONS } from '../types/user';
import type { TaskScope, User, UserPermission, UserRole } from '../types/user';

/** Every action known to the system. */
export const ALL_PERMISSIONS: readonly UserPermission[] = USER_PERMISSIONS;

/** Actions a soldier may perform (scoped to their own assigned tasks). */
export const SOLDIER_PERMISSIONS: readonly UserPermission[] = ['VIEW_TASKS', 'UPDATE_TASK_STATUS'];

/** Baseline for a work manager. Anything else must be granted explicitly. */
export const DEFAULT_ADMIN_PERMISSIONS: readonly UserPermission[] = [
  'VIEW_TASKS',
  'VIEW_ALL_TASKS',
  'CREATE_TASK',
  'EDIT_TASK',
  'ASSIGN_TASK',
  'UPDATE_TASK_STATUS',
  'MANAGE_VEHICLES',
  'MANAGE_SOLDIERS',
  'VIEW_REPORTS',
  // A manager reads the override ledger for the assignments they answer for.
  'VIEW_AUDIT_LOGS',
];

/**
 * Reserved to super-admins — an override flag can never grant these.
 *
 * These three are what separates מנהל על from a commander: the user directory,
 * the code somebody signs in with, and the system configuration. A מ"פ runs the
 * day and still cannot reach any of them, which is the whole point of the
 * separation — see `src/core/access-levels.ts`.
 */
export const SUPER_ADMIN_ONLY: readonly UserPermission[] = [
  'MANAGE_USERS',
  'RESET_USER_PIN',
  'MANAGE_SYSTEM_SETTINGS',
];

/** The starting point for each role, before per-account overrides. */
export const ROLE_BASE_PERMISSIONS: Record<UserRole, readonly UserPermission[]> = {
  super_admin: ALL_PERMISSIONS,
  admin: DEFAULT_ADMIN_PERMISSIONS,
  soldier: SOLDIER_PERMISSIONS,
};

export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'מנהל על',
  admin: 'מנהל עבודה',
  soldier: 'חייל',
};

export const PERMISSION_LABELS: Record<UserPermission, string> = {
  VIEW_TASKS: 'צפייה במשימות',
  VIEW_ALL_TASKS: 'צפייה בכל משימות היחידה',
  CREATE_TASK: 'יצירת משימה',
  EDIT_TASK: 'עריכת משימה',
  DELETE_TASK: 'מחיקת משימה',
  ASSIGN_TASK: 'שיבוץ משימה',
  OVERRIDE_ASSIGNMENT: 'עקיפת שיבוץ קיים',
  UPDATE_TASK_STATUS: 'עדכון סטטוס משימה',
  MANAGE_VEHICLES: 'ניהול רכבים',
  MANAGE_SOLDIERS: 'ניהול חיילים',
  MANAGE_USERS: 'ניהול משתמשים והרשאות',
  RESET_USER_PIN: 'איפוס קוד כניסה למשתמש',
  APPROVE_DEVICE: 'אישור מכשירים',
  DISCONNECT_DEVICE: 'ניתוק מכשיר',
  MANAGE_SYSTEM_SETTINGS: 'הגדרות מערכת',
  VIEW_REPORTS: 'צפייה בדוחות',
  VIEW_AUDIT_LOGS: 'צפייה ביומן הביקורת',
  EXPORT_DATA: 'ייצוא נתונים',
};

/** Raised by {@link assertPermission}. */
export class PermissionDeniedError extends Error {
  readonly action: UserPermission;

  constructor(action: UserPermission) {
    super(`Permission denied: ${action}`);
    this.name = 'PermissionDeniedError';
    this.action = action;
  }
}

/** An account only carries permissions while it is approved and active. */
export function isAccountUsable(user: User | null | undefined): boolean {
  if (!user) return false;
  return user.isApproved && user.status === 'active';
}

/** Central guard — the only function allowed to answer permission questions. */
export function hasPermission(user: User | null | undefined, action: UserPermission): boolean {
  if (!user) return false;
  if (!ALL_PERMISSIONS.includes(action)) return false;
  if (!isAccountUsable(user)) return false;

  if (user.role === 'super_admin') return true;
  if (user.role === 'soldier') return SOLDIER_PERMISSIONS.includes(action);

  // admin — role baseline tuned by custom configuration flags.
  const override = user.permissionOverrides?.[action];
  if (override === false) return false;
  if (override === true) return !SUPER_ADMIN_ONLY.includes(action);
  return DEFAULT_ADMIN_PERMISSIONS.includes(action);
}

export function hasAllPermissions(
  user: User | null | undefined,
  actions: readonly UserPermission[],
): boolean {
  return actions.every((action) => hasPermission(user, action));
}

export function hasAnyPermission(
  user: User | null | undefined,
  actions: readonly UserPermission[],
): boolean {
  return actions.some((action) => hasPermission(user, action));
}

/** The full effective permission set for a user. */
export function permissionsFor(user: User | null | undefined): UserPermission[] {
  return ALL_PERMISSIONS.filter((action) => hasPermission(user, action));
}

/**
 * Task-scoped check. A soldier may only act on tasks assigned to them;
 * managers are bound by {@link hasPermission} alone.
 */
export function canActOnTask(
  user: User | null | undefined,
  action: UserPermission,
  task: TaskScope,
): boolean {
  if (!user || !hasPermission(user, action)) return false;
  if (user.role !== 'soldier') return true;
  return task.assignedUserIds.includes(user.id);
}

/**
 * Internal manager notes on a task ("הערות פנימיות למנהלים") are readable only
 * by an account that sees the whole unit's board. The tasks UI asks this
 * question here rather than testing roles on its own.
 */
export function canViewInternalNotes(user: User | null | undefined): boolean {
  return hasPermission(user, 'VIEW_ALL_TASKS');
}

/**
 * Whether the application chrome shows a role badge for `subject` to `viewer`.
 *
 * The "מנהל על" indicator is the owner's own marker, not a label the unit wears
 * about: it appears when the super-admin is looking at their own header, and
 * nowhere else. A soldier's or a work manager's header carries no role badge at
 * all — the management screens are where roles are read, and they name them
 * explicitly rather than through this badge.
 */
export function canSeeRoleBadge(
  viewer: User | null | undefined,
  subject: User | null | undefined = viewer,
): boolean {
  if (!viewer || !subject) return false;
  if (subject.role !== 'super_admin') return false;
  return viewer.id === subject.id;
}

/** Throwing variant, for guarding mutations. */
export function assertPermission(user: User | null | undefined, action: UserPermission): void {
  if (!hasPermission(user, action)) throw new PermissionDeniedError(action);
}
